import { DiagramNode, DiagramSpec, splitAnswer } from "./diagramSpec";

/**
 * The change request a proposed scenario turns into when the user orders it.
 *
 * Ordering is a support ticket today (until the NaaS project orders services
 * directly), so the draft is written for the person who will provision it:
 * what is new, which existing services it touches, and what Archi noted —
 * capacity it found missing, options it gave. The user reviews and sends it;
 * nothing is created from here.
 */
export interface OrderDraft {
  title: string;
  description: string;
}

const line = (node: DiagramNode) =>
  `- ${node.label}${node.detail ? ` — ${node.detail}` : ""}${node.ref ? ` (${node.ref})` : ""}`;

/** The answer's words, without its drawing blocks. */
export function answerNotes(answer: string): string {
  return splitAnswer(answer)
    .flatMap((part) => (part.kind === "text" ? [part.text.trim()] : []))
    .filter(Boolean)
    .join("\n\n");
}

export function draftOrder(spec: DiagramSpec, notes: string): OrderDraft {
  const byId = new Map(spec.nodes.map((n) => [n.id, n]));
  const added = spec.nodes.filter((n) => n.planned);
  const newLinks = spec.edges.filter((e) => e.planned);
  // The existing services the new ones attach to: what provisioning must touch.
  const touchedIds = new Set(newLinks.flatMap((e) => [e.from, e.to]));
  const touched = spec.nodes.filter((n) => !n.planned && touchedIds.has(n.id));

  const sections = [
    "Requested from Archi, from a proposed scenario.",
    added.length > 0 ? ["New:", ...added.map(line)].join("\n") : null,
    newLinks.length > 0
      ? [
          "New connections:",
          ...newLinks.map(
            (e) =>
              `- ${byId.get(e.from)?.label ?? e.from} → ${byId.get(e.to)?.label ?? e.to}${e.label ? ` (${e.label})` : ""}`,
          ),
        ].join("\n")
      : null,
    touched.length > 0
      ? ["Existing services involved:", ...touched.map(line)].join("\n")
      : null,
    notes ? `Archi's notes:\n${notes}` : null,
  ];

  return {
    title: `Order: ${spec.title ?? "proposed change"}`.slice(0, 120),
    description: sections.filter(Boolean).join("\n\n"),
  };
}
